import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FolderOpen, Loader2, Mic, Upload, Users, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import MyAssetsDialog from "@/components/MyAssetsDialog";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import posthog from "posthog-js";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  availableCharacterPresets,
  type CharacterPreset,
} from "@shared/characterPresets";
import { assignPromptImageTags } from "@shared/promptImageTags";

// Curated pre-generated performers users can pick instead of uploading a
// photo. Selecting one adds a normal character reference (no upload, no
// classification — the roster already knows each preset's appearance).
const PRESET_GALLERY = availableCharacterPresets();

export type ReferenceRole = "character" | "vibe";

export type ReferenceImage = {
  imageUrl: string;
  name: string;
  role: ReferenceRole;
  /** Apparent gender of the person (character images only) — powers the
   *  vocal-gender conflict warning in the workspace voice step. */
  appearance?: "male" | "female" | "unknown";
  /** True while Gemini is deciding the role after upload. */
  classifying?: boolean;
  /** People the classifier saw as the photo's subject (foreground, in
   *  focus). 2+ drives the "only one person will be cast" warning;
   *  undefined/null = classifier couldn't tell, never warn. */
  subjectCount?: number | null;
  /** Prompt handle ("image1") — the user can write "@image1 is the singer"
   *  in the prompt to say how this exact photo is used. Assigned once and
   *  kept stable across reorders/removals (shared/promptImageTags.ts). */
  tag?: string;
};

/** Flat list used by the picker; split into character[] + vibe by the caller
 *  when sending to the backend. First entry with role="character" is treated
 *  as the primary character by the generator. */
export type ReferenceSelections = {
  references?: ReferenceImage[];
};

const MAX_REFERENCES = 3;

interface ReferencePickerModalProps {
  open: boolean;
  onClose: () => void;
  selected: ReferenceSelections;
  onChange: (next: ReferenceSelections) => void;
  onRequireAuth?: () => void;
  onUnauthenticatedFile?: (file: File) => Promise<void> | void;
  /**
   * Singing-video mode: exactly one character photo. Picking a preset or
   * uploading replaces the current selection instead of stacking, and the
   * character/vibe role toggle is locked to character (the provider animates
   * one face — a "vibe" reference has no meaning there).
   */
  singleCharacter?: boolean;
}

export default function ReferencePickerModal({
  open,
  onClose,
  selected,
  onChange,
  onRequireAuth,
  onUnauthenticatedFile,
  singleCharacter = false,
}: ReferencePickerModalProps) {
  const { isAuthenticated, user } = useAuth();
  const [draft, setDraft] = useState<ReferenceSelections>(selected);
  const [uploading, setUploading] = useState(false);
  const [assetsOpen, setAssetsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadMutation = trpc.upload.uploadAiMusicVideoAsset.useMutation();
  const classifyMutation =
    trpc.director.classifyReferenceImage.useMutation();

  const refs = draft.references ?? [];
  // Display-only numbering; the committed list gets the same tags on
  // Continue so the prompt's "@imageN" always means the photo shown here.
  const refTags = assignPromptImageTags(refs).map((r) => r.tag ?? "");
  const maxRefs = singleCharacter ? 1 : MAX_REFERENCES;
  // In single-character mode a new pick replaces the selection, so the
  // picker is never "full" in the blocking sense.
  const full = !singleCharacter && refs.length >= maxRefs;

  useEffect(() => {
    if (!open) return;
    if (singleCharacter) {
      // Selections can arrive from the multi-reference (music video) mode;
      // show only the one image the singing flow will actually use.
      const list = selected.references ?? [];
      const first = list.find((r) => r.role === "character") ?? list[0];
      setDraft({ references: first ? [first] : [] });
      return;
    }
    setDraft(selected);
  }, [open, selected, singleCharacter]);

  const updateRef = (imageUrl: string, patch: Partial<ReferenceImage>) => {
    setDraft((prev) => ({
      references: (prev.references ?? []).map((r) =>
        r.imageUrl === imageUrl ? { ...r, ...patch } : r,
      ),
    }));
  };

  const removeRef = (imageUrl: string) => {
    setDraft((prev) => ({
      references: (prev.references ?? []).filter(
        (r) => r.imageUrl !== imageUrl,
      ),
    }));
  };

  const toggleRole = (imageUrl: string) => {
    setDraft((prev) => ({
      references: (prev.references ?? []).map((r) =>
        r.imageUrl === imageUrl
          ? { ...r, role: r.role === "character" ? "vibe" : "character" }
          : r,
      ),
    }));
  };

  // The generator treats the first character reference as the lead singer
  // (lip-sync performer). Making a character the lead = moving it to the
  // front so that contract stays a plain array order.
  const makeLead = (imageUrl: string) => {
    setDraft((prev) => {
      const list = prev.references ?? [];
      const target = list.find((r) => r.imageUrl === imageUrl);
      if (!target) return prev;
      return {
        references: [target, ...list.filter((r) => r.imageUrl !== imageUrl)],
      };
    });
  };

  const leadImageUrl = refs.find((r) => r.role === "character")?.imageUrl;
  const characterCount = refs.filter((r) => r.role === "character").length;
  // Continue must wait for in-flight classification: committing early would
  // freeze the optimistic role and drop the appearance, since late results
  // only patch this modal's draft (which resets on next open).
  const classifyingCount = refs.filter((r) => r.classifying).length;

  const handleUploadClick = () => {
    if (full) {
      toast.error(`Up to ${MAX_REFERENCES} references.`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }
    if (!isAuthenticated || !user) {
      try {
        await onUnauthenticatedFile?.(file);
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : "Could not save this image before sign-in.",
        );
        return;
      }
      onRequireAuth?.();
      onClose();
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const result = await uploadMutation.mutateAsync({
        fileName: file.name,
        fileData: base64,
        contentType: file.type,
        assetType: "image",
      });
      const image: ReferenceImage = {
        imageUrl: result.url,
        name: file.name.replace(/\.[^.]+$/, "") || "Reference",
        role: "character", // optimistic default; overwritten by classifier
        classifying: true,
      };
      setDraft((prev) => ({
        references: singleCharacter
          ? [image]
          : [...(prev.references ?? []), image],
      }));

      // Fire-and-forget classify. Updates the pill in place when it lands.
      // Single-character mode keeps the classifier's appearance (drives the
      // vocal-gender conflict check) but pins the role to character.
      classifyMutation
        .mutateAsync({ imageUrl: image.imageUrl })
        .then(({ role, label, appearance, subjectCount }) => {
          updateRef(image.imageUrl, {
            role: singleCharacter ? "character" : role,
            classifying: false,
            name: label || image.name,
            appearance: appearance ?? "unknown",
            subjectCount: subjectCount ?? null,
          });
          if (typeof subjectCount === "number" && subjectCount >= 2) {
            posthog.capture("character_photo_multi_person_warning", {
              surface: singleCharacter
                ? "web_reference_picker_single"
                : "web_reference_picker",
              subject_count: subjectCount,
            });
          }
        })
        .catch(() => {
          updateRef(image.imageUrl, { classifying: false });
        });
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Upload failed. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  };

  const togglePreset = (preset: CharacterPreset) => {
    if (refs.some((r) => r.imageUrl === preset.imageUrl)) {
      removeRef(preset.imageUrl);
      return;
    }
    if (full) {
      toast.error(`Up to ${MAX_REFERENCES} references.`);
      return;
    }
    const next: ReferenceImage = {
      imageUrl: preset.imageUrl,
      name: preset.name,
      role: "character",
      appearance: preset.appearance === "none" ? "unknown" : preset.appearance,
    };
    setDraft((prev) => ({
      references: singleCharacter ? [next] : [...(prev.references ?? []), next],
    }));
  };

  // Images picked from My Assets are already hosted urls — no upload, but
  // they still go through the classifier (same as device uploads) so the
  // role/appearance pills and the vocal-gender check work on them.
  const handleAssetsConfirm = (
    items: Array<{ url: string; label: string }>,
  ) => {
    setAssetsOpen(false);
    const current = draft.references ?? [];
    const fresh = items.filter(
      (item) => !current.some((r) => r.imageUrl === item.url),
    );
    const room = singleCharacter ? 1 : MAX_REFERENCES - current.length;
    const picked = fresh.slice(0, Math.max(0, room));
    if (picked.length === 0) return;
    const images: ReferenceImage[] = picked.map((item) => ({
      imageUrl: item.url,
      name: item.label || "Reference",
      role: "character", // optimistic default; overwritten by classifier
      classifying: true,
    }));
    setDraft((prev) => ({
      references: singleCharacter
        ? [images[0]]
        : [...(prev.references ?? []), ...images],
    }));
    for (const image of images) {
      classifyMutation
        .mutateAsync({ imageUrl: image.imageUrl })
        .then(({ role, label, appearance, subjectCount }) => {
          updateRef(image.imageUrl, {
            role: singleCharacter ? "character" : role,
            classifying: false,
            name: label || image.name,
            appearance: appearance ?? "unknown",
            subjectCount: subjectCount ?? null,
          });
        })
        .catch(() => {
          updateRef(image.imageUrl, { classifying: false });
        });
    }
  };

  const handleContinue = () => {
    onChange({ references: assignPromptImageTags(draft.references ?? []) });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        showCloseButton={false}
        // While the My Assets overlay is up, Escape belongs to it — without
        // this, Radix's own (earlier-registered) listener closes this dialog
        // on the same keypress.
        onEscapeKeyDown={(e) => {
          if (assetsOpen) e.preventDefault();
        }}
        className="max-w-2xl max-h-[85vh] overflow-hidden p-0 gap-0 bg-[oklch(0.14_0.02_270)] border-white/10 flex flex-col"
      >
        <div className="flex items-start justify-between px-6 pt-6 pb-3 shrink-0">
          <div className="flex-1 text-center">
            <h2 className="text-xl font-semibold text-white">
              {singleCharacter ? "Singer photo" : "Reference images"}
            </h2>
            <p className="text-[11px] uppercase tracking-wider text-white/40 mt-1">
              {singleCharacter
                ? "Optional · One photo"
                : `Optional · Up to ${MAX_REFERENCES}`}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 shrink-0"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="border-b border-white/10 shrink-0" />

        {/* Scrollable body — the footer must stay visible even when the
            preset gallery makes the content taller than the dialog. */}
        <div className="px-6 py-6 space-y-4 flex-1 min-h-0 overflow-y-auto">
          {/* Thumbnail row */}
          {refs.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {refs.map((r, refIndex) => (
                <div
                  key={r.imageUrl}
                  className="relative aspect-square rounded-xl overflow-hidden border border-white/10 bg-white/[0.03]"
                >
                  <img
                    src={r.imageUrl}
                    alt={r.name}
                    className="h-full w-full object-cover"
                  />
                  {!singleCharacter && refTags[refIndex] ? (
                    <span
                      className="absolute top-9 left-1.5 h-5 px-1.5 rounded-md bg-black/75 border border-white/15 flex items-center font-mono text-[10px] text-white/85"
                      title="Type this in your prompt to say how the photo is used"
                    >
                      @{refTags[refIndex]}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Remove ${r.name}`}
                    onClick={() => removeRef(r.imageUrl)}
                    className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-white/80 hover:text-white"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  {r.role === "character" &&
                    (r.imageUrl === leadImageUrl ? (
                      <span className="absolute top-1.5 left-1.5 h-6 px-2 rounded-full bg-violet-500/90 border border-white/20 flex items-center gap-1 text-[10px] font-semibold text-white">
                        <Mic className="h-3 w-3" />
                        Lead singer
                      </span>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Make ${r.name} the lead singer`}
                        onClick={() => makeLead(r.imageUrl)}
                        className="absolute top-1.5 left-1.5 h-6 px-2 rounded-full bg-black/80 border border-white/20 flex items-center gap-1 text-[10px] font-medium text-white/70 hover:text-white hover:border-white/40"
                      >
                        <Mic className="h-3 w-3" />
                        Make lead
                      </button>
                    ))}
                  <button
                    type="button"
                    onClick={() =>
                      !r.classifying &&
                      !singleCharacter &&
                      toggleRole(r.imageUrl)
                    }
                    disabled={r.classifying || singleCharacter}
                    className="absolute bottom-1.5 left-1.5 right-1.5 h-6 px-2 rounded-full bg-black/80 border border-white/15 flex items-center justify-center gap-1 text-[11px] font-medium text-white/90 hover:bg-black/90 disabled:cursor-default"
                    title={
                      singleCharacter ? undefined : "Click to change role"
                    }
                  >
                    {r.classifying ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Classifying…
                      </>
                    ) : (
                      <>
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            r.role === "character"
                              ? "bg-emerald-400"
                              : "bg-amber-400"
                          }`}
                        />
                        {r.role === "character" ? "Character" : "Vibe"}
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
          {!singleCharacter && refs.length > 0 && (
            <p className="text-[11px] text-white/50 text-center">
              Tag a photo in your prompt to say how it's used, e.g. “@
              {refTags[0] || "image1"} is the singer
              {refTags[1] ? `, @${refTags[1]} is the location` : ""}”.
            </p>
          )}
          {characterCount > 0 && (
            <p className="text-[11px] text-white/40 text-center">
              {characterCount > 1
                ? "The Lead singer performs the vocals in singing videos. Tap “Make lead” to change who sings."
                : "This character is the lead singer — they perform the vocals in singing videos."}
            </p>
          )}
          {refs.some(
            (r) =>
              r.role === "character" &&
              typeof r.subjectCount === "number" &&
              r.subjectCount >= 2,
          ) && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3.5 py-3 text-[13px] leading-snug text-amber-100"
            >
              <Users className="h-4 w-4 mt-0.5 shrink-0 text-amber-300" />
              <span>
                One of these photos shows more than one person. Your video is
                built around <strong>one main character</strong>, so only one
                of them will appear.{" "}
                {singleCharacter
                  ? "For best results, use a photo with just that person."
                  : "To include everyone, add each person as their own separate photo."}
              </span>
            </div>
          )}

          {/* Upload dropzone */}
          <button
            type="button"
            onClick={handleUploadClick}
            disabled={uploading || full}
            className="w-full aspect-[5/2] rounded-xl border border-dashed border-white/15 bg-white/[0.03] flex flex-col items-center justify-center gap-2 text-white/70 hover:border-white/30 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Upload className="h-6 w-6" />
            )}
            <span className="text-sm">
              {full
                ? `Max ${MAX_REFERENCES} references added`
                : refs.length === 0
                  ? "Upload a photo"
                  : singleCharacter
                    ? "Replace photo"
                    : "Add another"}
            </span>
            <span className="text-[11px] text-white/40 px-8 text-center">
              {singleCharacter
                ? "One clear, front-facing portrait — this face performs the vocals."
                : "Faces, places, film stills, moodboards — we'll tag each one and use it to guide the video."}
            </span>
          </button>

          {/* My Assets — reuse images from earlier projects (auth only:
              the library is the user's own uploads + generations). */}
          {isAuthenticated && (
            <button
              type="button"
              onClick={() => {
                if (full) {
                  toast.error(`Up to ${MAX_REFERENCES} references.`);
                  return;
                }
                setAssetsOpen(true);
              }}
              disabled={full}
              className="w-full h-11 rounded-xl border border-white/15 bg-white/[0.03] flex items-center justify-center gap-2 text-sm text-white/70 hover:border-white/30 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FolderOpen className="h-4 w-4" />
              Choose from My Assets
            </button>
          )}

          {/* Preset character gallery */}
          {PRESET_GALLERY.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] uppercase tracking-wider text-white/40 text-center">
                No photo? Pick a singer
              </p>
              <div className="pr-1">
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {PRESET_GALLERY.map((preset) => {
                    const isSelected = refs.some(
                      (r) => r.imageUrl === preset.imageUrl,
                    );
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => togglePreset(preset)}
                        className={`relative aspect-[3/4] rounded-xl overflow-hidden border text-left transition ${
                          isSelected
                            ? "border-emerald-400"
                            : "border-white/10 hover:border-white/25"
                        }`}
                        aria-pressed={isSelected}
                        aria-label={
                          isSelected
                            ? `Remove ${preset.name}`
                            : `Use ${preset.name}`
                        }
                      >
                        <img
                          src={preset.imageUrl}
                          alt={preset.name}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-1">
                          <div className="text-[10px] text-white font-medium truncate">
                            {preset.name}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10 shrink-0">
          <Button
            variant="ghost"
            onClick={onClose}
            className="flex-1 bg-white/5 hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={classifyingCount > 0}
            className="flex-1 bg-white/10 hover:bg-white/15 text-white"
          >
            {classifyingCount > 0 ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Classifying…
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </div>

        <MyAssetsDialog
          open={assetsOpen}
          onClose={() => setAssetsOpen(false)}
          onConfirm={handleAssetsConfirm}
          maxSelect={singleCharacter ? 1 : Math.max(1, maxRefs - refs.length)}
          excludeUrls={refs.map((r) => r.imageUrl)}
        />
      </DialogContent>
    </Dialog>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Unexpected reader result"));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.readAsDataURL(file);
  });
}
